import {
	useEffect,
	useRef,
	useState,
	type CSSProperties,
	type ReactNode,
} from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { PRONTUARIO_AUDIO_VISUALIZER_GLOW_CLASS } from '../../constants/prontuarioTheme';

const BAR_COUNT = 3;
const DEFAULT_HEIGHTS = [6, 11, 6];
const CLICK_GLOW_VISIBLE_MS = 1_000;

const randomBetween = (min: number, max: number) =>
	min + Math.random() * (max - min);

const buildRandomHeights = (): number[] => {
	const center = randomBetween(9, 14);
	const side = center * randomBetween(0.48, 0.72);
	const sideJitter = side * randomBetween(0.88, 1.08);

	return [sideJitter, center, side];
};

type SessionCallProntuarioAudioVisualizerSize = 'default' | 'large';

type SessionCallProntuarioAudioVisualizerProps = {
	className?: string;
	ariaLabel?: string;
	size?: SessionCallProntuarioAudioVisualizerSize;
	/** Quando dentro de um botão com rótulo próprio (ex.: badge do prontuário). */
	decorative?: boolean;
	animated?: boolean;
	interactive?: boolean;
	onBarsClick?: () => void;
	/** Exibe divisória + slot da direita (ex.: menu na seta). */
	chevronAction?: ReactNode;
	/** Quando o painel de transcrição está aberto (só afeta estilos externos). */
	toggleOpen?: boolean;
};

const SIZE_CLASS: Record<SessionCallProntuarioAudioVisualizerSize, string> = {
	default: 'h-[26px] min-w-[54px] px-3',
	large: 'h-9 min-w-[64px] px-4',
};

const BAR_WIDTH_PX: Record<SessionCallProntuarioAudioVisualizerSize, number> = {
	default: 3,
	large: 3,
};

const HEIGHT_SCALE: Record<SessionCallProntuarioAudioVisualizerSize, number> = {
	default: 1,
	large: 1.2,
};

const barSlotStyle = (widthPx: number): CSSProperties => ({
	width: widthPx,
	minWidth: widthPx,
	maxWidth: widthPx,
	flexShrink: 0,
	flexGrow: 0,
});

const barStyle = (
	widthPx: number,
	heightPx: number,
	transitionMs: number,
): CSSProperties => ({
	width: widthPx,
	minWidth: widthPx,
	maxWidth: widthPx,
	height: heightPx,
	borderRadius: widthPx / 2,
	flexShrink: 0,
	transition: `height ${transitionMs}ms ease-out`,
});

const SessionCallProntuarioAudioVisualizer = ({
	className,
	ariaLabel = 'Áudio da sessão',
	size = 'default',
	decorative = false,
	animated = true,
	interactive = false,
	onBarsClick,
	chevronAction,
	toggleOpen,
}: SessionCallProntuarioAudioVisualizerProps) => {
	const [heights, setHeights] = useState(DEFAULT_HEIGHTS);
	const [transitionMs, setTransitionMs] = useState(160);
	const [pulseKey, setPulseKey] = useState(0);
	const [showClickGlow, setShowClickGlow] = useState(false);
	const glowTimeoutRef = useRef<number | null>(null);
	const barWidthPx = BAR_WIDTH_PX[size];
	const minBarHeightPx = barWidthPx + 2;

	useEffect(() => {
		if (!animated) {
			setHeights(DEFAULT_HEIGHTS);
			setTransitionMs(0);
			return;
		}

		let cancelled = false;
		let timeoutId: ReturnType<typeof setTimeout> | undefined;

		const tick = () => {
			if (cancelled) return;

			const burst = Math.random() < 0.28;
			setHeights(buildRandomHeights());
			setTransitionMs(
				burst ? randomBetween(80, 130) : randomBetween(140, 260),
			);

			const waitMs = burst
				? randomBetween(60, 110)
				: randomBetween(100, 280);

			timeoutId = setTimeout(tick, waitMs);
		};

		tick();

		return () => {
			cancelled = true;
			if (timeoutId) clearTimeout(timeoutId);
		};
	}, [animated]);

	useEffect(
		() => () => {
			if (glowTimeoutRef.current) {
				window.clearTimeout(glowTimeoutRef.current);
			}
		},
		[],
	);

	const hasChevronSlot = chevronAction != null;

	const shellClassName = cn(
		'box-border flex shrink-0 items-center rounded-full bg-[#3c4043] transition-[background-color,box-shadow] duration-300',
		!hasChevronSlot && 'justify-center',
		hasChevronSlot
			? size === 'large'
				? 'h-9 w-[90px]'
				: 'h-[26px] w-[78px]'
			: SIZE_CLASS[size],
		showClickGlow && PRONTUARIO_AUDIO_VISUALIZER_GLOW_CLASS,
		interactive &&
			'relative overflow-hidden transition-[color,background-color,box-shadow] duration-300',
		className,
	);

	const bars = (
		<span className="relative z-10 flex items-center gap-[3px]" aria-hidden>
			{heights.slice(0, BAR_COUNT).map((height, index) => {
				const barHeightPx = Math.max(
					minBarHeightPx,
					height * HEIGHT_SCALE[size],
				);

				return (
					<span
						key={index}
						className="flex items-center justify-center"
						style={barSlotStyle(barWidthPx)}
					>
						<span
							className="bg-[#8ab4f8]"
							style={barStyle(
								barWidthPx,
								barHeightPx,
								transitionMs,
							)}
						/>
					</span>
				);
			})}
		</span>
	);

	const handleBarsClick = () => {
		setPulseKey((current) => current + 1);
		setShowClickGlow(true);

		if (glowTimeoutRef.current) {
			window.clearTimeout(glowTimeoutRef.current);
		}

		glowTimeoutRef.current = window.setTimeout(() => {
			glowTimeoutRef.current = null;
			setShowClickGlow(false);
		}, CLICK_GLOW_VISIBLE_MS);

		onBarsClick?.();
	};

	if (interactive) {
		const interactiveShellClassName = cn(
			shellClassName,
			'cursor-pointer border-0 p-0 transition-colors hover:bg-white/[0.06] active:bg-white/[0.09]',
		);

		if (hasChevronSlot) {
			return (
				<button
					type="button"
					onClick={handleBarsClick}
					aria-label={ariaLabel}
					aria-expanded={toggleOpen}
					className={interactiveShellClassName}
				>
					{pulseKey > 0 ? (
						<motion.span
							key={pulseKey}
							aria-hidden
							className="pointer-events-none absolute inset-0 rounded-full bg-[#8ab4f8]/45"
							initial={{ opacity: 0.65, scale: 1 }}
							animate={{ opacity: 0, scale: 1.5 }}
							transition={{ duration: 0.55, ease: 'easeOut' }}
						/>
					) : null}
					<span className="relative z-10 flex h-full min-w-0 flex-[6] items-center justify-center">
						{bars}
					</span>
					<span
						className="relative z-10 flex h-full min-w-0 flex-[3] items-center justify-center"
						aria-hidden
					>
						{chevronAction}
					</span>
				</button>
			);
		}

		return (
			<div className={shellClassName}>
				{pulseKey > 0 ? (
					<motion.span
						key={pulseKey}
						aria-hidden
						className="pointer-events-none absolute inset-0 rounded-full bg-[#8ab4f8]/45"
						initial={{ opacity: 0.65, scale: 1 }}
						animate={{ opacity: 0, scale: 1.5 }}
						transition={{ duration: 0.55, ease: 'easeOut' }}
					/>
				) : null}
				<button
					type="button"
					onClick={handleBarsClick}
					aria-label={ariaLabel}
					className={cn(
						'relative z-10 flex h-full w-full min-w-0 items-center justify-center rounded-full',
						'transition-colors hover:bg-white/[0.06] active:bg-white/[0.09]',
					)}
				>
					{bars}
				</button>
			</div>
		);
	}

	return (
		<div
			{...(decorative
				? { 'aria-hidden': true }
				: { role: 'img', 'aria-label': ariaLabel })}
			className={shellClassName}
		>
			{bars}
		</div>
	);
};

export { SessionCallProntuarioAudioVisualizer };
